import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { primeServices } from "@services/apis/Prime";
import { NtxBrlRatesData } from "@services/models/Prime/NtxBrlRatesModel";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

const formatRate = (value?: number) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "--";
  return num.toFixed(4);
};

const InvertibleEffectiveRate = ({
  rate,
  isLoading,
}: {
  rate?: number;
  isLoading: boolean;
}) => {
  const [showOriginal, setShowOriginal] = useState(false);
  const num = Number(rate);
  const hasRate = Number.isFinite(num) && num > 0;
  const displayed = showOriginal ? num : 1 / num;
  const leftSymbol = showOriginal ? "BRL" : "USDT";
  const rightSymbol = showOriginal ? "USDT" : "BRL";

  if (isLoading) {
    return <p className="text-text-base text-[13px] font-semibold">...</p>;
  }

  if (!hasRate) {
    return <p className="text-text-base text-[13px] font-semibold">--</p>;
  }

  return (
    <button
      type="button"
      onClick={() => setShowOriginal((current) => !current)}
      title="Click to invert rate"
      className="flex flex-row flex-wrap gap-[4px] items-center cursor-pointer rounded-md -ml-1 px-1 py-0.5 hover:bg-bg-page-body transition-colors"
    >
      <span className="text-text-base text-[13px] font-semibold">1</span>
      <span className="text-text-base text-[13px] font-semibold">
        {leftSymbol}
      </span>
      <span className="material-symbols-outlined text-[13px] text-icon-base">
        sync_alt
      </span>
      <span className="text-text-base text-[13px] font-semibold">
        {formatRate(displayed)}
      </span>
      <span className="text-text-base text-[13px] font-semibold">
        {rightSymbol}
      </span>
    </button>
  );
};

const isValidSpread = (value: string) => {
  if (value.trim() === "" || value === "-" || value === "." || value === "-.") {
    return false;
  }
  const num = Number(value);
  return Number.isFinite(num) && num > -100 && num < 100;
};

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const err = error as {
    response?: { data?: { msg?: string } };
    message?: string;
  };
  return err?.response?.data?.msg || err?.message || fallback;
};

interface BrNtxRatesSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const BrNtxRatesSlider = ({
  isOpen,
  onClose,
  onSaved,
}: BrNtxRatesSliderProps) => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const handleErrorRef = useRef(handleError);
  handleErrorRef.current = handleError;
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [rates, setRates] = useState<NtxBrlRatesData | null>(null);
  const [payInSpread, setPayInSpread] = useState("");
  const [payOutSpread, setPayOutSpread] = useState("");
  const [isSavingSpreads, setIsSavingSpreads] = useState(false);
  const [isRefreshingRates, setIsRefreshingRates] = useState(false);

  const seedSpreadInputs = (data: NtxBrlRatesData) => {
    setPayInSpread(String(data.payIn.spread));
    setPayOutSpread(String(data.payOut.spread));
  };

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const loadRates = async () => {
      setIsRefreshingRates(true);
      try {
        const response = await primeServices.getNtxBrlRates();
        if (cancelled) return;
        setRates(response.data);
        seedSpreadInputs(response.data);
      } catch (err) {
        if (!cancelled) {
          handleErrorRef.current({
            message: getApiErrorMessage(err, "Failed to fetch BRL NTX rates"),
          });
        }
      } finally {
        if (!cancelled) {
          setIsRefreshingRates(false);
        }
      }
    };

    loadRates();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleClose = () => {
    if (rates) {
      seedSpreadInputs(rates);
    }
    onClose();
  };

  const handleSaveSpreads = async () => {
    if (!isValidSpread(payInSpread) || !isValidSpread(payOutSpread)) {
      handleError({
        message:
          "payInSpread and payOutSpread are required and must be numbers greater than -100 and less than 100",
      });
      return;
    }

    setIsSavingSpreads(true);
    try {
      const response = await primeServices.setNtxBrlSpreads({
        payInSpread: Number(payInSpread),
        payOutSpread: Number(payOutSpread),
      });
      handleSuccess({
        message: response.msg || "BRL NTX spreads updated successfully",
      });
      onSaved?.();
      handleClose();
    } catch (err) {
      handleError({
        message: getApiErrorMessage(err, "Failed to update BRL NTX spreads"),
      });
    } finally {
      setIsSavingSpreads(false);
    }
  };

  const canSaveSpreads =
    isValidSpread(payInSpread) &&
    isValidSpread(payOutSpread) &&
    (!rates ||
      Number(payInSpread) !== Number(rates.payIn.spread) ||
      Number(payOutSpread) !== Number(rates.payOut.spread));

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading="BRL NTX Rates"
      onClose={handleClose}
      onClickCross={handleClose}
      showConfirm={false}
      isMobile={isMobile}
    >
      <div
        className={`flex flex-col gap-4 p-3 ${isMobile ? "min-h-[calc(80vh-100px)]" : ""}`}
      >
        <div className="rounded-lg border border-border-subtle bg-bg-base px-3 py-3">
          <p className="text-text-subtle text-[11px] font-medium mb-2">
            BRL / USDT
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-text-subtle text-[10px]">Pay in effective</p>
              <InvertibleEffectiveRate
                key={`pay-in-${isOpen}`}
                rate={rates?.payIn.effectiveRate}
                isLoading={isRefreshingRates}
              />
            </div>
            <div>
              <p className="text-text-subtle text-[10px]">Pay out effective</p>
              <InvertibleEffectiveRate
                key={`pay-out-${isOpen}`}
                rate={rates?.payOut.effectiveRate}
                isLoading={isRefreshingRates}
              />
            </div>
            <div>
              <p className="text-text-subtle text-[10px]">Raw rate</p>
              <p className="text-text-base text-[13px] font-semibold">
                {isRefreshingRates ? "..." : formatRate(rates?.payIn.rawRate)}
              </p>
            </div>
            <div>
              <p className="text-text-subtle text-[10px]">Current spreads</p>
              <p className="text-text-base text-[13px] font-semibold">
                {rates
                  ? `${rates.payIn.spread} / ${rates.payOut.spread}`
                  : "--"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-bg-base px-3 py-3">
          <p className="text-text-subtle text-[11px] font-medium">
            Update spreads
          </p>
          <GlobalTextInputField
            title="Pay in spread"
            type="number"
            allowDecimal={true}
            allowNegative={true}
            value={payInSpread}
            onChange={(e) => setPayInSpread(e.target.value)}
            placeholder="e.g. -3.00"
          />
          <GlobalTextInputField
            title="Pay out spread"
            type="number"
            allowDecimal={true}
            allowNegative={true}
            value={payOutSpread}
            onChange={(e) => setPayOutSpread(e.target.value)}
            placeholder="e.g. -3.00"
          />
          <p className="text-text-subtle text-[11px]">
            Both values must be numbers greater than -100 and less than 100.
            Negative spreads such as -3.00 are allowed.
          </p>
        </div>

        <CustomButton
          text="SAVE SPREADS"
          isActive={canSaveSpreads && !isSavingSpreads}
          onClick={handleSaveSpreads}
          isButtonLoading={isSavingSpreads}
        />
      </div>
    </SlidingPanel>
  );
};

export default BrNtxRatesSlider;
